import { useEffect, useRef, useState } from "react";

// Respect OS "reduce motion" preference.
export function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Animate a number from 0 (or previous value) up to `target`.
 * Lightweight rAF tween, no dependency. Re-tweens when target changes.
 * @param {number} target
 * @param {object} opts { duration=800, delay=0 }
 */
export function useCountUp(target, opts = {}) {
  const { duration = 800, delay = 0 } = opts;
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef(null);
  const toRef = useRef(target);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(target);
      return undefined;
    }
    const from = fromRef.current;
    toRef.current = target;
    const start = performance.now() + delay;

    const tick = (now) => {
      const t = Math.min(1, Math.max(0, (now - start) / duration));
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + (target - from) * eased;
      setValue(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      fromRef.current = toRef.current; // so next tween starts from last target
    };
  }, [target, duration, delay]);

  return value;
}

export default useCountUp;
